import core from 'core';
import actions from 'actions';
import selectors from 'selectors';

/**
 * @typedef {Object} UI.DrawingOverlayManager.Creator
 * @property {number} id The creator's ID
 * @property {string} full_name The creator's full name
 * @property {string} role The creator's role
 */

/**
 * @typedef {Object} UI.DrawingOverlayManager.DrawingOverlay
 * @property {number} id The drawing overlay ID
 * @property {number} document_id The document ID
 * @property {number} project_id The project ID
 * @property {number} priority_id The priority ID
 * @property {number} discipline_id The discipline ID
 * @property {number} status_id The status ID
 * @property {number} [creator_id] The creator ID (optional)
 * @property {number} [assignee_id] The assignee ID (optional)
 * @property {string} summary The issue summary
 * @property {string} full_text The full issue text
 * @property {Object} [assignee] The assignee details (optional)
 * @property {Object} [creator] The creator details (optional)
 * @property {boolean} [is_favourite] Whether the issue is marked as favorite (optional)
 * @property {Array} comments The issue comments
 * @property {string} created_at The creation timestamp
 * @property {string} updated_at The last update timestamp
 * @property {string} xfdf The XFDF data of the annotation
 */

/**
 * @class
 * @name DrawingOverlayManager
 * @memberof UI
 * @extends Core.EventHandler
 */
class DrawingOverlayManager {
  initialize(store, annotationManager) {
    this.store = store;
    this.annotationManager = annotationManager;
    /**
     * the key represents the event name
     * the value is an array of callbacks that will be called when the given key event is triggered
     * @type {Object.<string, Function[]>}
     * @ignore
     */
    this.events = {};
  }

  /**
   * Loads the drawing overlays that will be displayed in the drawing overlays overlay
   * @method UI.DrawingOverlayManager#loadDrawingOverlays
   * @param {UI.DrawingOverlayManager.DrawingOverlay[]} drawingOverlays An array of drawing overlays
   */
  loadDrawingOverlays(drawingOverlays) {
    // load drawing overlays in the pdf viewer
    drawingOverlays.forEach(drawingOverlay => {
      const width = drawingOverlay.x1 - drawingOverlay.x0;
      const height = drawingOverlay.y1 - drawingOverlay.y0;

      const Annotations = window.Core.Annotations;

      const rectAnnot = new Annotations.RectangleAnnotation({
        PageNumber: drawingOverlay.drawing_sheet.page_number,
        X: drawingOverlay.x0,
        Y: drawingOverlay.y0,
        Width: width,
        Height: height,
        // StrokeColor: '#A3E635',
        StrokeColor: new Annotations.Color(163, 230, 53, 1),
        // Author: this.annotationManager.getCurrentUser(), // TODO: get the creator
      });

      // Store custom data as a JSON string
      rectAnnot.setCustomData('annotationCategory', 'drawingOverlay');
      rectAnnot.setCustomData('drawingName', drawingOverlay.title);
      rectAnnot.setCustomData('drawingOverlayId', drawingOverlay.id);
      rectAnnot.setCustomData('drawingId', drawingOverlay.id);
      rectAnnot.setCustomData('drawingSheetId', drawingOverlay.drawing_sheet_id);

      // Optionally set an ID for easier reference
      rectAnnot.Id = `do_${drawingOverlay.id}`;

      // Add and render the annotation
      this.annotationManager.addAnnotation(rectAnnot);
      this.annotationManager.redrawAnnotation(rectAnnot);
    });
  }

  deleteDrawingOverlay(annotation) {
    this.annotationManager.deleteAnnotation(annotation, { force: true });
    console.log(`Annotation with ID ${annotation.Id} has been deleted.`);
  }

  setEditingDrawingOverlayId(id) {
    this.store.dispatch(actions.setEditingDrawingOverlayId(id));
  }

  on(eventName, fn) {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    this.events[eventName].push(fn);
    return this;
  }

  off(eventName, fn) {
    if (!this.events) {
      return this;
    }

    if (typeof eventName === 'undefined') {
      this.events = {};
    }

    if (!this.events[eventName]) {
      return this;
    }

    if (fn) {
      this.events[eventName] = this.events[eventName].filter(handler => handler !== fn);
    } else {
      this.events[eventName] = [];
    }

    return this;
  }

  one(eventName, fn) {
    const origFn = fn;

    fn = (...args) => {
      this.off(eventName, fn);
      origFn.apply(this, args);
    };

    return this.on(eventName, fn);
  }

  trigger(eventName, ...data) {
    if (!this.events) {
      return this;
    }

    if (!this.events[eventName]) {
      return this;
    }

    // normalize data if only an array is passed in as data
    // for example: trigger('issueUpdate', [annotations])
    // data will be passed to the handler: (annotations) => {}
    if (data.length === 1 && Array.isArray(data[0])) {
      data = data[0];
    }

    this.events[eventName].forEach(fn => {
      fn.call(this, ...data);
    });

    return this;
  }

  addEventListener(...args) {
    this.on(...args);
  }

  removeEventListener(...args) {
    this.off(...args);
  }
}

/**
 * Triggered when issues have been updated (added, deleted, modified).
 * Attach like instance.drawingOverlayManager.on('issueUpdate', callback)
 * @name UI.DrawingOverlayManager#issueUpdate
 * @event
 * @param {UI.DrawingOverlayManager.Issue[]} issues The issues that were updated
 * @param {'add'|'modify'|'delete'} action The action that occurred (add, delete, modify)
 * @param {Object} [options] Additional options
 */

export default new DrawingOverlayManager();
export { DrawingOverlayManager };
